/*!
 * Copyright (c) 2021-2022 WeatherLayers.com
 *
 * WeatherLayers Client
 *
 * Demo - https://demo.weatherlayers.com/
 * Docs - https://docs.weatherlayers.com/
 */

import * as t from 'geotiff';
function e(t, e, a) {
  return (
    e in t
      ? Object.defineProperty(t, e, {
          value: a,
          enumerable: !0,
          configurable: !0,
          writable: !0,
        })
      : (t[e] = a),
    t
  );
}
async function a(e) {
  const a = await t.fromUrl(e, { allowFullFile: !0, blockSize: 10485760 }),
    n = await a.getImage(0);
  return {
    data: (function (t) {
      let e =
        arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : void 0;
      if (null == e) return t;
      const a = t.slice(0);
      for (let t = 0; t < a.length; t++)
        Math.abs(a[t] - e) < 2 * Number.EPSILON && (a[t] = NaN);
      return a;
    })(await n.readRasters({ interleave: !0 }), n.getGDALNoData()),
    width: n.getWidth(),
    height: n.getHeight(),
  };
}
function n(t) {
  if (t.includes('.png'))
    return (async function (t) {
      const e = await (await fetch(t)).blob(),
        a = new Image();
      (a.src = URL.createObjectURL(e)), await a.decode();
      const n = document.createElement('canvas');
      (n.width = a.width), (n.height = a.height);
      const i = n.getContext('2d');
      i.drawImage(a, 0, 0);
      const c = i.getImageData(0, 0, n.width, n.height),
        { data: o, width: r, height: s } = c;
      return { data: o, width: r, height: s };
    })(t);
  if (t.includes('.tif')) return a(t);
  throw new Error('Unsupported data format');
}
const i = { url: 'https://catalog.weatherlayers.com', dataFormat: 'byte.png' };
function c(t, e) {
  const a = e.get(t);
  if (a) return a;
  const n = (async function (t) {
    return (await fetch(t)).text();
  })(t);
  return (
    e.set(t, n),
    n.then((a) => {
      e.set(t, a);
    }),
    n
  );
}
function o(t, e) {
  const a = e.get(t);
  if (a) return a;
  const n = (async function (t) {
    return (await fetch(t)).json();
  })(t);
  return (
    e.set(t, n),
    n.then((a) => {
      e.set(t, a);
    }),
    n
  );
}
function r(t) {
  let e =
    arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : void 0;
  const a = new URLSearchParams();
  !t.includes('access_token=') && e && a.set('access_token', e),
    t.includes('version=') || a.set('version', '2022.10.0');
  const n = a.toString(),
    i = ''.concat(t).concat(n ? '?'.concat(n) : '');
  return i;
}
function s(t) {
  return t.links
    .filter((t) => 'item' === t.rel)
    .map((t) => t.datetime)
    .filter((t) => !!t);
}
class l {
  constructor(t) {
    e(this, 'config', void 0),
      e(this, 'cache', new Map()),
      (this.config = { ...i, ...t });
  }
  setConfig(t) {
    this.config = { ...this.config, ...t };
  }
  async loadStacCatalog() {
    return o(
      r(''.concat(this.config.url, '/catalog'), this.config.accessToken),
      this.cache,
    );
  }
  async loadStacCollection(t) {
    return o(
      r(
        ''.concat(this.config.url, '/catalog/').concat(t),
        this.config.accessToken,
      ),
      this.cache,
    );
  }
  async loadStacCollectionPalette(t) {
    const e = await this.loadStacCollection(t),
      a = Object.values(e.assets).find(
        (t) => t.roles.includes('palette') && 'text/plain' === t.type,
      );
    if (!a) throw new Error('Palette asset not found');
    return c(r(a.href, this.config.accessToken), this.cache);
  }
  async loadStacItem(t, e) {
    const a = (await this.loadStacCollection(t)).links.find(
      (t) => 'item' === t.rel && t.datetime === e,
    );
    if (!a) throw new Error('Item '.concat(e, ' not found'));
    return o(r(a.href, this.config.accessToken), this.cache);
  }
  async loadStacItemData(t, e, a) {
    return (function (t, e) {
      const a = e.get(t);
      if (a) return a;
      const i = n(t);
      return (
        e.set(t, i),
        i.then((a) => {
          e.set(t, a);
        }),
        i
      );
    })(
      r(
        (await this.loadStacItem(t, e)).assets['data.'.concat(a)].href,
        this.config.accessToken,
      ),
      this.cache,
    );
  }
  async loadCatalog() {
    const t = (await this.loadStacCatalog()).links
      .filter((t) => 'child' === t.rel)
      .map((t) => t.id)
      .filter((t) => !!t);
    return (
      await Promise.all(
        t.map(async (t) =>
          (await this.loadStacCollection(t)).links
            .filter((t) => 'child' === t.rel)
            .map((t) => t.id)
            .filter((t) => !!t),
        ),
      )
    ).flat();
  }
  async loadDataset(t) {
    let { attributionLinkClass: e } =
      arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : {};
    const a = await this.loadStacCollection(t);
    return {
      title: (function (t) {
        return t.title;
      })(a),
      unitFormat: (function (t) {
        return t['weatherLayers:units'][0];
      })(a),
      attribution: (function (t) {
        let e =
          arguments.length > 1 && void 0 !== arguments[1]
            ? arguments[1]
            : void 0;
        const a = t.providers.find((t) => t.roles.includes('producer')),
          n = t.providers.find((t) => t.roles.includes('processor'));
        return [
          ...(a
            ? [
                '<a href="'
                  .concat(a.url, '"')
                  .concat(e ? ' class="'.concat(e, '"') : '', '>')
                  .concat(a.name, '</a>'),
              ]
            : []),
          ...(n
            ? [
                '<a href="'
                  .concat(n.url, '"')
                  .concat(e ? ' class="'.concat(e, '"') : '', '>')
                  .concat(n.name, '</a>'),
              ]
            : []),
        ].join(' via ');
      })(a, e),
      datetimes: s(a),
      palette: await this.loadStacCollectionPalette(t),
    };
  }
  async loadDatasetData(t, e) {
    let { datetimeInterpolate: a = !1 } =
      arguments.length > 2 && void 0 !== arguments[2] ? arguments[2] : {};
    const n = await this.loadStacCollection(t),
      i = s(n),
      c = (function (t, e) {
        return [...t].reverse().find((t) => t <= e);
      })(i, e),
      o = a
        ? (function (t, e) {
            return t.find((t) => t >= e);
          })(i, e)
        : null;
    if (!c) throw new Error('No data found');
    const [r, l] = await Promise.all([
      this.loadStacItemData(t, c, this.config.dataFormat),
      a && o ? this.loadStacItemData(t, o, this.config.dataFormat) : null,
    ]);
    return {
      image: r,
      image2: l,
      imageWeight:
        a && o
          ? (function (t, e, a) {
              if (a <= t) return 0;
              if (a >= e) return 1;
              {
                const n = new Date(t),
                  i = new Date(e);
                return (
                  (new Date(a).getTime() - n.getTime()) /
                  (i.getTime() - n.getTime())
                );
              }
            })(c, o, e)
          : 0,
      imageType: (function (t) {
        return t['weatherLayers:imageType'];
      })(n),
      imageUnscale:
        r.data instanceof Uint8Array || r.data instanceof Uint8ClampedArray
          ? (function (t) {
              return t['weatherLayers:imageUnscale'];
            })(n)
          : null,
      bounds: (function (t) {
        return t.extent.spatial.bbox[0];
      })(n),
    };
  }
}
export { l as Client };
//# sourceMappingURL=weatherlayers-client.es.min.js.map
